import React from 'react';
import { Appointment, Case } from '../../types';
import {
  formatNextAppointmentChipLabel,
  getAvatarColorClass,
  getInitials,
  getStatusBadgeClass,
  getStatusLabel,
} from './dashboardUtils';
import SessionRoad from './SessionRoad';
import {
  translateIssueType,
  getIssueTypeBadgeClass,
} from '../Cases/casesUtils';
import { t } from '../../utils/translations';
import Button from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';

interface DashboardCaseCardProps {
  caseItem: Case;
  reportCount: number;
  lastActivityLabel: string;
  nextAppointment?: Appointment;
  appointmentBySession?: Record<number, Appointment>;
  pulseAppointment?: boolean;
  onView: () => void;
  onAddNote: () => void;
  onAddReport: () => void;
  onSchedule: (sessionNumber?: number) => void;
  onOpenAppointment?: () => void;
  onOpenReport?: (session: number) => void;
  onPrepare?: (session: number, appointment: Appointment) => void;
}

const DashboardCaseCard: React.FC<DashboardCaseCardProps> = ({
  caseItem,
  reportCount,
  lastActivityLabel,
  nextAppointment,
  appointmentBySession,
  pulseAppointment = false,
  onView,
  onAddNote,
  onAddReport,
  onSchedule,
  onOpenAppointment,
  onOpenReport,
  onPrepare,
}) => {
  return (
    // The hover lift makes this card a stacking context, so the session-road
    // bubble can only clear the next card's pulsing chip if the card itself is
    // raised. z-10 is enough to beat the sibling cards while staying below the
    // sticky topbar (z-30). No static z-index: without one the card stays out of
    // the stacking order on touch, where there is no hover.
    <div className="relative min-w-0 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition duration-200 ease-out hover:z-10 hover:-translate-y-0.5 hover:shadow-md focus-within:z-10 sm:p-5">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${getAvatarColorClass(
            caseItem.counseledName
          )}`}
        >
          {getInitials(caseItem.counseledName)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-slate-900">{caseItem.counseledName}</p>
                {nextAppointment && (
                  <button
                    type="button"
                    onClick={onOpenAppointment}
                    className={`${
                      pulseAppointment ? 'animate-schedule-pulse ' : ''
                    }shrink-0 rounded-full bg-brand-600 px-2 py-0.5 text-xs font-medium text-white transition hover:bg-brand-700`}
                    title={t.dashboard.upcomingAppointments}
                  >
                    {formatNextAppointmentChipLabel(nextAppointment)}
                  </button>
                )}
              </div>
              <p className="text-sm text-slate-500">
                {caseItem.assignedCounselorName ?? 'Nealocat'}
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {caseItem.priority === 'high' && (
              <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                {t.cases.priorityBadge}
              </span>
            )}
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeClass(
                caseItem.status
              )}`}
            >
              {getStatusLabel(caseItem.status)}
            </span>
            {caseItem.issueTypes.map((issueType) => (
              <span
                key={issueType}
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${getIssueTypeBadgeClass(issueType)}`}
              >
                {translateIssueType(issueType)}
              </span>
            ))}
            <span className="text-xs text-slate-400">Ultima activitate: {lastActivityLabel}</span>
          </div>

          <div className="mt-3">
            <SessionRoad
              reportCount={reportCount}
              nextAppointment={nextAppointment}
              appointmentBySession={appointmentBySession}
              onOpenReport={onOpenReport}
              onAddReport={onAddReport}
              onSchedule={(session) => onSchedule(session)}
              onPrepare={onPrepare}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="primary" onClick={onAddReport}>
              {t.cases.cockpit.addReportCta}
            </Button>
            <Button size="sm" onClick={onAddNote}>
              {t.cases.cockpit.addNoteCta}
            </Button>
            <Button size="sm" icon={actionIcon.schedule} onClick={() => onSchedule()}>
              {t.cases.cockpit.scheduleCta}
            </Button>
            <Button size="sm" icon={actionIcon.view} onClick={onView}>
              {t.common.view}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardCaseCard;
